// VPC-mode helpers.
//
// In VPC deployments the frontend authenticates against the enclave backend via
// `?api_key=...` instead of Firebase. A fetch monkey-patch in `main.tsx` injects
// the key as an `X-API-Key` header. These helpers let the route guards treat a
// present api_key as equivalent to an authenticated session, so protected
// routes don't redirect to `/login` when Firebase is stubbed.
//
// All behavior here is gated on `VITE_VPC_MODE === "1"` (or `"true"`). In SaaS
// mode everything here is a no-op.

const VPC_API_KEY_STORAGE_KEY = "nomadicai_vpc_api_key";

export const isVpcMode = (): boolean => {
  const flag = import.meta.env.VITE_VPC_MODE;
  return flag === "1" || flag === "true";
};

/** Read the api_key from URL first, then fall back to localStorage. No-op in SaaS. */
export const getVpcApiKey = (): string | null => {
  if (typeof window === "undefined") return null;
  if (!isVpcMode()) return null;
  const fromUrl = new URLSearchParams(window.location.search).get("api_key");
  if (fromUrl) return fromUrl;
  try {
    return window.localStorage.getItem(VPC_API_KEY_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Persist the api_key from the current URL into localStorage, if present. No-op in SaaS.
 *
 * The SaaS guard matters: without it, a SaaS user who clicks any URL with
 * `?api_key=...` (shared link, debug session, tampered bookmark) would have the
 * key written to localStorage, then main.tsx's fetch monkey-patch would
 * activate on every subsequent page load — silently stripping Authorization
 * and injecting X-API-Key — breaking Firebase auth until the user clears
 * localStorage. Gate at the entry point instead of relying on downstream
 * helpers to re-check.
 */
export const persistVpcApiKeyFromUrl = (): void => {
  if (typeof window === "undefined") return;
  if (!isVpcMode()) return;
  const fromUrl = new URLSearchParams(window.location.search).get("api_key");
  if (!fromUrl) return;
  try {
    window.localStorage.setItem(VPC_API_KEY_STORAGE_KEY, fromUrl);
  } catch {
    // best-effort only
  }
  // Take it out of the address bar once it is stored: otherwise the credential
  // rides along in browser history, in any link the operator copies, and in
  // every referrer the page sends.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("api_key");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // best-effort only
  }
};

/** Store an api_key obtained from the VPC login form. No-op in SaaS. */
export const setVpcApiKey = (key: string): void => {
  if (typeof window === "undefined" || !isVpcMode()) return;
  try {
    window.localStorage.setItem(VPC_API_KEY_STORAGE_KEY, key);
  } catch {
    // best-effort only
  }
};

/** Forget the stored api_key (VPC logout). No-op in SaaS. */
export const clearVpcApiKey = (): void => {
  if (typeof window === "undefined" || !isVpcMode()) return;
  try {
    window.localStorage.removeItem(VPC_API_KEY_STORAGE_KEY);
  } catch {
    // best-effort only
  }
  // getVpcApiKey reads the URL first, so a rejected key in ?api_key= would keep
  // hasVpcApiKey() true and let the route guards mount a protected page with a
  // credential the server has already refused.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("api_key")) {
      url.searchParams.delete("api_key");
      window.history.replaceState({}, "", url.toString());
    }
  } catch {
    // best-effort only
  }
};

/** True when VPC mode is on and an api_key is available (URL or storage). */
export const hasVpcApiKey = (): boolean => {
  return isVpcMode() && !!getVpcApiKey();
};
